import * as React from 'react';
import { selectedPlatforms } from 'shared/src/domain/line-item/line-item-channels-filters';
import { formatMoney } from 'shared/src/utils/money/money-utils';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';
import { Lock } from '@/icons/lock';
import { MediaIcon } from '@/icons/media-icon';

type Props = {
  mediaPlatformIds: number[];
  plannedUnitCost: number | undefined;
  unitPriceTypeId: number | undefined;
  onMediaUpdateClick: () => void;
};

export function LockedMediaTraderFields({
  mediaPlatformIds,
  plannedUnitCost,
  unitPriceTypeId,
  onMediaUpdateClick
}: Props) {
  const { channels } = useChannelRatesContext();
  const platforms = selectedPlatforms(channels, mediaPlatformIds);

  return (
    <div className="flex flex-col gap-2">
      <div>
        <span>Editing fields in this section should be done using</span>
        <MediaUpdateButton onClick={onMediaUpdateClick} />
        <span>in the Execution Plan view. Those changes will be reflected here.</span>
      </div>
      <LockedField
        label="Media Platforms"
        value={
          <div className="flex flex-wrap gap-2">
            {platforms.map(p => (
              <LockedItem value={p.name} key={p.id} />
            ))}
          </div>
        }
      />
      <LockedField
        label="Planned Unit Cost"
        value={formatPlannedUnitCost(plannedUnitCost, unitPriceTypeId)}
      />
    </div>
  );
}

function formatPlannedUnitCost(
  plannedUnitCost: number | undefined,
  unitPriceTypeId: number | undefined
): string {
  if (plannedUnitCost === undefined) return formatMoney(undefined);
  const isCPM = unitPriceTypeId === 1;
  return isCPM ? `${formatMoney(plannedUnitCost * 1000)} CPM` : formatMoney(plannedUnitCost);
}

function LockedField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded bg-gray-800 text-white flex justify-between py-2 px-3 items-center w-full min-w-0">
      <div className="font-semibold text-sm text-gray-400 w-36">{label}</div>
      <div className="flex-1 pr-1 w-full min-w-0">{value}</div>
      <div className="border-l-[1px] border-gray-400 pl-2">
        <Lock />
      </div>
    </div>
  );
}

function LockedItem({ value }: { value: React.ReactNode | string | undefined }) {
  return <div className="rounded bg-slate-700 w-fit inline-block px-2 py-1 text-sm">{value}</div>;
}

function MediaUpdateButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="ml-2 mr-2 mb-[-10px] inline-flex h-[28px] items-center gap-1 rounded-sm px-2 text-sm border border-[#3B6FEC] text-[#3B6FEC] hover:border-blue-700 hover:text-blue-700">
      <MediaIcon />
      <span className="ml-2">Media Update</span>
    </button>
  );
}
